/**
 * Chooses logo tile background:
 * - light green for near-black marks on transparency
 * - dark green for near-white marks on transparency
 * - card surface for transparent coloured/non-extreme mono marks
 * - default forest green for mostly-opaque raster marks.
 */

export type LogoTileBackdrop = 'green' | 'surface' | 'monoLight' | 'monoDark';

/** Max dimension when downsampling before pixel scan (performance + enough detail). */
export const LOGO_BACKDROP_SAMPLE_MAX_PX = 56;

const ALPHA_TRANSPARENT = 28;
const MIN_OPAQUE_PIXELS = 12;
/** Need enough transparent pixels to treat asset as "logo on alpha" vs a full-bleed photo. */
const MIN_TRANSPARENT_RATIO = 0.055;
/** Opaque pixels with clear hue spread → colour logo. */
const MAX_CHROMA_RATIO_FOR_MONO = 0.11;
const CHROMA_SPREAD = 42;

/**
 * Classifies sampled RGBA pixels. Assumes `imageData` is from a downscaled logo decode.
 */
export function classifyLogoTileBackdropFromImageData(imageData: ImageData): LogoTileBackdrop {
  const { data, width, height } = imageData;
  const total = width * height;
  if (total < 1) return 'green';

  let transparent = 0;
  let opaque = 0;
  let chromaPixels = 0;
  let sumL = 0;

  for (let i = 0; i < data.length; i += 4) {
    const a = data[i + 3] ?? 0;
    if (a < ALPHA_TRANSPARENT) {
      transparent++;
      continue;
    }
    opaque++;
    const r = data[i] ?? 0;
    const g = data[i + 1] ?? 0;
    const b = data[i + 2] ?? 0;
    const spread = Math.max(r, g, b) - Math.min(r, g, b);
    if (spread > CHROMA_SPREAD) chromaPixels++;
    sumL += (r + g + b) / 3;
  }

  if (opaque < MIN_OPAQUE_PIXELS) return 'green';

  const transparentRatio = transparent / total;
  if (transparentRatio < MIN_TRANSPARENT_RATIO) return 'green';

  const chromaRatio = chromaPixels / opaque;
  if (chromaRatio > MAX_CHROMA_RATIO_FOR_MONO) return 'surface';

  const meanL = sumL / opaque;
  const nearWhite = meanL >= 198;
  const nearBlack = meanL <= 62;
  if (nearWhite && chromaRatio <= MAX_CHROMA_RATIO_FOR_MONO) return 'monoDark';
  if (nearBlack && chromaRatio <= MAX_CHROMA_RATIO_FOR_MONO) return 'monoLight';

  return 'surface';
}

/**
 * Draws the image into a small canvas and classifies pixels. Returns `'green'` if the canvas is
 * tainted, empty, or any error occurs (e.g. cross-origin without CORS).
 */
export function classifyLogoTileBackdropFromHtmlImage(img: HTMLImageElement): LogoTileBackdrop {
  if (typeof document === 'undefined') return 'green';
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  if (!w || !h) return 'green';

  const scale = Math.min(1, LOGO_BACKDROP_SAMPLE_MAX_PX / Math.max(w, h));
  const cw = Math.max(1, Math.round(w * scale));
  const ch = Math.max(1, Math.round(h * scale));

  const canvas = document.createElement('canvas');
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return 'green';

  try {
    ctx.drawImage(img, 0, 0, cw, ch);
    const imageData = ctx.getImageData(0, 0, cw, ch);
    return classifyLogoTileBackdropFromImageData(imageData);
  } catch {
    return 'green';
  }
}
